import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface PortalUser {
  name: string;
  tenantId: string;
  /** Present on the magic-link path, which knows the customer up front. A
   *  social sign-in learns the tenant from discovery and never needs these. */
  id?: string;
  email?: string;
}

export interface TenantOption {
  tenantId: string;
  name?: string;
  logo?: string;
  confirmed: boolean;
}

/**
 * Sessions are owned by the Firebase SDK — it holds the refresh token and
 * renews the ID token on its own, so nothing token-shaped is persisted here.
 */
interface AuthState {
  user: PortalUser | null;
  /** Sent as X-Tenant-Id; decides which linked tenant requests act in. */
  activeTenantId: string | null;
  tenants: TenantOption[];
  /**
   * False until Firebase has restored (or ruled out) a session. Guards the
   * reload race where persisted state claims a session the SDK has not yet
   * rehydrated.
   */
  authReady: boolean;

  loginWithFirebase: (user: PortalUser) => void;
  setTenants: (tenants: TenantOption[]) => void;
  setActiveTenant: (tenantId: string) => void;
  setAuthReady: (ready: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    set => ({
      user: null,
      activeTenantId: null,
      tenants: [],
      authReady: false,

      loginWithFirebase: user =>
        set({
          user,
          activeTenantId: user.tenantId,
          authReady: true,
        }),

      setTenants: tenants => set({ tenants }),

      setActiveTenant: tenantId =>
        set(state => ({
          activeTenantId: tenantId,
          user: state.user ? { ...state.user, tenantId } : state.user,
        })),

      setAuthReady: authReady => set({ authReady }),

      logout: () => {
        set({
          user: null,
          activeTenantId: null,
          tenants: [],
          authReady: true,
        });
      },
    }),
    {
      name: 'portal-auth-storage',
      // authReady is per-load, never restored: persisting it would defeat the
      // guard it exists to provide.
      partialize: state => ({
        user: state.user,
        activeTenantId: state.activeTenantId,
        tenants: state.tenants,
      }),
    }
  )
);

export const getActiveTenantId = (): string | null => useAuthStore.getState().activeTenantId;
